export const FORMATTERS = {
  /**
   * Converts a number into a "K" format for values over 1000.
   * @example 1200 -> "1.2K" | 500 -> "500"
   */
  formatThousands: (value: number) => {
    if (value === 0) return '0';
    if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
    return value.toLocaleString();
  },

  /**
   * Formats a date string into a long-form US English date.
   * @example "01/04/2026" -> "January 4, 2026"
   */
  formatDate: (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  },

  /**
   * Formats a value using standard US number formatting (adds commas).
   * @example 1000000 -> "1,000,000"
   */
  formattedNumberType: (value: any) => {
    return new Intl.NumberFormat('en-US').format(value);
  },

  /**
   * Formats a numeric value as a percentage string with 2 decimal places.
   * Divides the input by 100 before formatting.
   * @example 50 -> "50.00%"
   */
  formatPercent: (value: any) => {
    const num = Number.parseFloat(value);
    const safe = Number.isNaN(num) ? 0 : num;

    return new Intl.NumberFormat('en-US', {
      style: 'percent',
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    }).format(safe / 100);
  },

  /**
   * Uses native Intl formatting to convert numbers to compact "K/M" notation.
   * @example 1500 -> "1.5K"
   */
  formatCompactNumber: (value: number) => {
    return new Intl.NumberFormat('en', {
      notation: 'compact',
      compactDisplay: 'short',
      maximumFractionDigits: 2,
    }).format(value);
  },

  /**
   * Converts a "YYYY-MM" string into a shortened month and year.
   * @example "2024-05" -> "May 2024"
   */
  formatMonthLabel: (value: string): string => {
    const [year, month] = value.split('-');
    if (!year || !month) return value;

    const monthIndex = Number(month) - 1;
    if (monthIndex < 0 || monthIndex > 11) return value;

    const monthShort = new Intl.DateTimeFormat('en-US', {
      month: 'short',
    }).format(new Date(2000, monthIndex, 1));

    return `${monthShort} ${year}`;
  },

  /**
   * Converts a total number of seconds into a HH:MM:SS duration string.
   * @example 3661 -> "01:01:01"
   */
  formatTime: (totalSeconds: number): string => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = Math.floor(totalSeconds % 60);
    const pad = (num: number) => num.toString().padStart(2, '0');
    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  },

  /**
   * Parses a numeric string into a height format (Feet'Inches).
   * @example "509" -> "5'09" | "6" -> "6'"
   */
  formatHeight: (raw: string): string => {
    const digits = raw.replace(/\D/g, '').slice(0, 3);
    if (digits.length === 0) return '';
    const feet = digits[0];
    const inches = digits.slice(1);
    if (!inches || /^0+$/.test(inches)) {
      return `${feet}'`;
    }
    return `${feet}'${inches}`;
  },

  /**
   * Removes all non-numeric characters from a string.
   * Useful for cleaning phone numbers for API storage.
   */
  formatSymbolsPhoneNumberToString: (input: string): string => {
    return input.replace(/\D/g, '');
  },

  /**
   * Converts a UTC date to a localized date/time string with timezone abbreviation.
   * @param utcDate The date to convert (string, number, or Date object)
   * @param timeZone Target timezone (defaults to system local)
   */
  formatUTCToLocal: (
    utcDate: string | number | Date,
    timeZone: string = Intl.DateTimeFormat().resolvedOptions().timeZone
  ): string => {
    let date: Date;
    if (typeof utcDate === 'string') {
      date = new Date(utcDate.endsWith('Z') ? utcDate : utcDate + 'Z');
    } else {
      date = new Date(utcDate);
    }

    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone,
      timeZoneName: 'short',
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
    const parts = formatter.formatToParts(date);
    const get = (type: Intl.DateTimeFormatPartTypes) =>
      parts.find((p) => p.type === type)?.value || '';

    return `${get('month')}/${get('day')}/${get('year')} ${get('hour')}:${get('minute')} ${get('timeZoneName')}`;
  },

  /**
   * Formats a numeric input string with comma separators as the user types.
   * @example "1000" -> "1,000"
   */
  formatWeightInput: (value: string): string => {
    const numeric = value.replace(/[^\d]/g, '');
    return numeric.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  },

  /**
   * Calculates an Estimated Time of Arrival (ETA) based on a start time and duration.
   * @param etaStartTimestamp The base timestamp (ms)
   * @param etaSeconds Duration in seconds to add
   * @param startEtaDate If true, uses current time as the base instead of the timestamp
   * @returns Formatted time "HH:MM"
   */
  formatEtaToTime: (
    etaStartTimestamp: number,
    etaSeconds?: number | null,
    startEtaDate?: boolean,
    timeZone: string = 'UTC'
  ) => {
    if (!etaSeconds) return '';

    const arrivalMs = startEtaDate
      ? Date.now() + etaSeconds * 1000
      : etaStartTimestamp + etaSeconds * 1000;

    return new Intl.DateTimeFormat('en-GB', {
      timeZone,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(new Date(arrivalMs));
  },

  /**
   * Converts a date string (MM/DD/YYYY) and optional time to start and end Unix timestamps.
   * @param dateOfService Format: "MM/DD/YYYY"
   * @param time Optional "HH:mm" time
   * @returns An object containing start and end timestamps (ms)
   */
  toISOFormat: (dateOfService: string, time?: string) => {
    const [month, day, year] = dateOfService.split('/');
    const paddedDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;

    let start: number;
    let end: number;

    if (time) {
      const iso = `${paddedDate}T${time}:00`;
      start = new Date(iso).getTime();
      end = start + 24 * 60 * 60 * 1000;
    } else {
      const startIso = `${paddedDate}T00:00:00`;
      const endIso = `${paddedDate}T23:59:59.999`;
      start = new Date(startIso).getTime();
      end = new Date(endIso).getTime();
    }
    return { start, end };
  },
  formatMonthYear: (date: any, locale = 'en-US') => {
    return new Intl.DateTimeFormat(locale, {
      month: 'short',
      year: 'numeric',
    }).format(new Date(date));
  },
  toDesiredCurrency: ({
    locales = 'en-US',
    currency = 'USD',
    value,
  }: {
    locales?: string;
    currency?: string;
    value: any;
  }) => {
    return new Intl.NumberFormat(locales, {
      style: 'currency',
      currency: currency,
    }).format(value);
  },
};
